import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {OmitStrict} from '@wix/devzai-utils-common';
import {
    arrayFlatten,
    arrayRemoveNullValues,
    hideableValueCreateHidden,
    hideableValueIsVisibleAndNotEqualValue,
    immerProduce,
    iterableMapToArray
} from '@wix/devzai-utils-common';
import type {DoppeActionTypeHoppBlogPost} from '../doppe-action-type-hopp-blog-post/doppe-action-type-hopp-blog-post';
import {
    convertDoppeDtoBlogPostToHoppBlogPostSettings,
    doppeActionTypeHoppBlogPostActionContentSettingsMetadata
} from '../doppe-action-type-hopp-blog-post/doppe-action-type-hopp-blog-post';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';


import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';


import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {bymoPageContextPropsResolveBusinessEntity} from '../../doppe-sdk/bymo-page/bymo-page-context-props';
import {
    DoppeActionLayout,
    DoppeBlogPostsExternalDataSource,
    doppeBlogPostsExternalDataSourceResolveBusinessEntityReferences,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsNotFound,
    DoppeDtoBlogPost,
    doppeDtoBlogPostGetMedia,
    DoppeExternalBlogPostProvider,
    doppeExternalBlogPostProviderResolveBusinessEntityReference,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionGetWithExternalDataSourceDefaultListSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemSettingsCreateDefault,
    doppeListActionListItemSettingsMigrateBaseSettings,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    DoppeListActionSettings,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionSettingsGetListItemCtaButtonsDefaultSettings,
    doppeListActionSettingsGetListItemSectionsDefaultSettings,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage
} from '../../client-server-common';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    DoppeLinkSpecEntityType,
    DoppeLinkSpecType
} from '../../client-server-common/types/doppe-action-cta-button';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';

export namespace DoppeActionTypeHoppBlogPosts {

    export interface BlogPostSettings extends
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        OmitStrict<DoppeActionTypeHoppBlogPost.ActionSettings, 'ctaButtons' | 'sections'> {
    }

    export interface ActionSettings extends
        DoppeListActionSettings<BlogPostSettings>,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeListActionSettings.WithExternalDataSourceSupport<DoppeBlogPostsExternalDataSource> {
        previewShowBlogPostDate: boolean;
        previewShowDescription: boolean;
        previewShowTitle: boolean;
    }
}

export const doppeActionTypeHoppBlogPosts = doppeActionTypeDefineMetadata<DoppeActionTypeHoppBlogPosts.ActionSettings>({
    id: 'io.bymo.action/hopp-blog-posts',
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, (actionSettings: DoppeActionTypeHoppBlogPosts.ActionSettings) => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            previewShowBlogPostDate = true,
            previewShowDescription = true,
            previewShowTitle = true,
            layout = DoppeActionLayout.Column,
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            previewShowBlogPostDate: previewShowBlogPostDate,
            previewShowDescription: previewShowDescription,
            previewShowTitle: previewShowTitle,
            ...doppeListActionGetWithExternalDataSourceDefaultListSettings(action.settings),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(action.settings),
            ...doppeListActionGetMainDetailsDefaultSettings({}, action.settings),
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings<DoppeActionTypeHoppBlogPosts.BlogPostSettings>({
                ...action.settings,
                layout: layout,
                listDataItems: listDataItems.map(blogPost => {

                    const {
                        blogPostTitle = '',
                        description = '',
                        blogPostImages = null,
                        blogPostDate = '',
                        blogPostLink = '',
                        blogPostProvider = null,
                        blogPostProviderId = null,
                        ribbon = hideableValueCreateHidden(''),
                        ...restBlogPost
                    } = blogPost;

                    return {
                        ...restBlogPost,
                        ...doppeActionGetMediaViewDefaultSettingsMigration(blogPost),
                        ...doppeListActionListItemSettingsMigrateBaseSettings(blogPost),
                        blogPostTitle: blogPostTitle,
                        description: description,
                        blogPostImages: blogPostImages,
                        blogPostDate: blogPostDate,
                        blogPostLink: blogPostLink,
                        blogPostProvider: blogPostProvider,
                        blogPostProviderId: blogPostProviderId,
                        ribbon: ribbon,
                        ...doppeListActionListItemWithCtaButtonsMigration(blogPost, false),
                        ...doppeListActionListItemWithSectionsMigration(blogPost),
                    }
                }),
            }),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
        } satisfies DoppeActionTypeHoppBlogPosts.ActionSettings;
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails()
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                mainImage: true,
                mainTitle: true
            }
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeActionTypeHoppBlogPostActionContentSettingsMetadata
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
            ribbon: true
        }
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: true,
    supportsListLayout: true,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(iterableMapToArray(actionSettings.listDataItems, (blogPost, skip) => {
            const mediaItems = arrayRemoveNullValues(blogPost.blogPostImages ?? []);
            return mediaItems.length > 0 ? mediaItems : skip;
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: actionSettings => {
        const blogPost = actionSettings.listDataItems[0];
        const mediaItems = blogPost ? arrayRemoveNullValues(blogPost.blogPostImages ?? []) : [];
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, mediaItems[0]);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    isConnectedToExternalDataSource: actionSettings => {
        return !!actionSettings.externalDataSource
    },
    resolveUsedBusinessEntities: actionSettings => {
        const externalDataSource = actionSettings.externalDataSource;
        if (externalDataSource) {
            return [
                doppeBlogPostsExternalDataSourceResolveBusinessEntityReferences(externalDataSource)
            ];
        } else {
            return iterableMapToArray(actionSettings.listDataItems, (blogPost, skip) => {
                return blogPost.blogPostProvider !== null && blogPost.blogPostProviderId !== null ?
                    doppeExternalBlogPostProviderResolveBusinessEntityReference(
                        blogPost.blogPostProvider,
                        blogPost.blogPostProviderId
                    ) : skip
            });
        }
    },
    resolveListItemsCount: actionSettings => {
        return actionSettings.externalDataSource ? actionSettings.externalDataSourceItemsCount : doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            actionSettings.listDataItems.forEach(blogPost => {
                if (blogPost.blogPostImages) {
                    blogPost.blogPostImages = blogPost.blogPostImages.map(
                        image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
                }
            });
        })
    },
    resolveListItems: (action, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            // Try to resolve the blog post entity from business entities data
            const blogPostEntity = item.blogPostProviderId ? bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                {businessEntity: DoppeBusinessEntity.HoppBlogPost, id: item.blogPostProviderId}
            ) : undefined;

            // Check if we have valid entity data
            const hasValidEntity = blogPostEntity && !doppeBusinessEntityDtoIsNotFound(blogPostEntity);

            // Resolve values from entity or fall back to stored settings
            const title = hasValidEntity
                ? (blogPostEntity.title ?? item.blogPostTitle)
                : item.blogPostTitle;

            const description = hasValidEntity
                ? (blogPostEntity.excerpt ?? item.description)
                : item.description;

            const ribbon = item.ribbon ?? hideableValueCreateHidden('');

            // Resolve image from entity or fall back to stored settings
            const entityImages = hasValidEntity ? doppeDtoBlogPostGetMedia(blogPostEntity) : null;
            const storedImages = arrayRemoveNullValues(item.blogPostImages ?? []);
            const image = (entityImages && entityImages.length > 0 ? entityImages[0] : storedImages[0]) ?? null;

            // Calculate URL from link spec
            const url = doppeActionCtaButtonsResolveMainURL(
                doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, item),
                {
                    sourceLinkSpec: item.blogPostProviderId ? {
                        type: DoppeLinkSpecType.Entity,
                        entityType: DoppeLinkSpecEntityType.BlogPost,
                        entityId: item.blogPostProviderId
                    } : null,
                    viewerLang: viewerLang,
                    bymoPageProps: bymoPageProps,
                    viewerPagesRoutesManager: viewerPagesRoutesManager,
                    bymoPageContextProps: bymoPageContextProps
                }
            ) ?? null;

            return {
                title: title,
                action: action,
                couponCode: hideableValueCreateHidden(''),
                description: hideableValueCreateHidden(description),
                image: image,
                itemId: item.id,
                ribbon: ribbon,
                url: url,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
});

export function convertDoppeDtoBlogPostToHoppBlogPostsSettings(blogPost: DoppeDtoBlogPost, blogPostProvider?: DoppeExternalBlogPostProvider): DoppeActionTypeHoppBlogPosts.BlogPostSettings {
    return {
        ...convertDoppeDtoBlogPostToHoppBlogPostSettings({
            blogPost: blogPost,
            blogPostProvider: blogPostProvider,
        }),
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
        ...doppeListActionListItemSettingsCreateDefault(blogPost.id),
    } satisfies DoppeActionTypeHoppBlogPosts.BlogPostSettings
} 